import React, { useContext, useEffect } from 'react';
import { defineMessages, T, t } from '@sm/intl';
import { Accordion } from '@wds/accordion';
import { Box } from '@wds/box';
import { IconCaretDown } from '@wds/icons';
import { List } from '@wds/list';
import { Menu } from '@wds/menu';
import { Responsive } from '@wds/responsive';
import {
  generateMetricsAttribute,
  MetricsTracker,
  USER_EVENTS,
} from '@sm/metrics';
import { NavButton } from '../../nav-button';
import { SubMenuItem } from '../../mobile-nav/SubMenuItem';
import getMyTeamMenuItems from '../../../../../../helpers/TeamMenuHelpers';
import { useHeaderTheme } from '../../../theme';
import HeaderContext from '../../../HeaderContext';
import useStyles from '../useStyles';

import { useAuthQueryLazyQuery } from './team-menu.generated';

const COPY = defineMessages({
  MY_TEAM: {
    id: 'TeamMenu.myTeam',
    defaultMessage: 'My Team',
    description: '[Type: Label][Vis.: low] - My team section of nav bar',
  },
});

const TeamMenu = () => {
  const theme = useHeaderTheme();
  const classes = useStyles({ theme });

  const { session, pageURL, pageURLPath } = useContext(HeaderContext);

  const [fetch, { loading, data }] = useAuthQueryLazyQuery();
  useEffect(() => {
    fetch();
  }, []);

  if (loading) return null;
  if (!data?.user) return null;
  const user = data?.user;

  const TrackMenuEvents = (opened: boolean) => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        url: pageURL,
        pageName: pageURLPath,
        amplitudeEvent: 'header clicked menu',
        menuName: 'team',
        opened,
      },
    });
  };

  const GenerateMetricsNavigationEvent = (navItem: String) =>
    generateMetricsAttribute({
      data: {
        url: pageURL,
        pageName: pageURLPath,
        amplitudeEvent: 'header clicked navigation',
        navItem,
      },
    });

  const teamMembership = user.teamMembership ?? {
    isAdmin: false,
    isEnterprise: false,
    memberType: '',
  };
  const canSeeTeamTab = teamMembership.isAdmin || teamMembership.isEnterprise;

  const title = t(COPY.MY_TEAM);
  const menuItems = getMyTeamMenuItems({ user, session });

  return canSeeTeamTab ? (
    <Responsive minWidth="xl">
      {matches =>
        matches ? (
          <Menu
            placement="bottom-left"
            noRules
            dense
            menuTrigger={
              <NavButton label="Open My Team menu">
                <T desc={COPY.MY_TEAM} />
                <Box ml={2}>
                  <IconCaretDown title="Dropdown arrow" />
                </Box>
              </NavButton>
            }
            key={title}
            elevation={9999}
            onChange={showMenu => {
              TrackMenuEvents(showMenu);
            }}
          >
            {menuItems.map(item => {
              return (
                <Menu.Item
                  key={`${item.name}-teamMenu`}
                  data-sm-metrics={GenerateMetricsNavigationEvent(
                    item.name.toLowerCase()
                  )}
                >
                  <a
                    href={item.path}
                    color="dark"
                    className={classes.teamMenuItem}
                  >
                    {item.name}
                  </a>
                </Menu.Item>
              );
            })}
          </Menu>
        ) : (
          <Accordion
            key="accordion-MyTeam"
            onChange={e => TrackMenuEvents(!e.length)}
          >
            <Accordion.Item title={title}>
              <List transparent>
                <Box className={classes.mobileTeams}>
                  {menuItems.map(item => {
                    return (
                      <SubMenuItem
                        metrics={GenerateMetricsNavigationEvent(
                          item.name.toLowerCase()
                        )}
                        {...item}
                      />
                    );
                  })}
                </Box>
              </List>
            </Accordion.Item>
          </Accordion>
        )
      }
    </Responsive>
  ) : null;
};

export default TeamMenu;
export { TeamMenu };
